import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../../../ui/Button";
import { Input } from "../../../ui/Input";
import * as S from "../Login.styled";
import { confirmEmail, requestEmailConfirmation } from "../../../Utils/authService";

const ConfirmEmailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get("email") || "");
  // Needs a setter: the confirmation email instructs the recipient to paste the
  // token, but until 2026-07-31 there was no input and no way to set it, so the
  // Confirm button was permanently disabled unless the token arrived in the URL.
  const [token, setToken] = useState(searchParams.get("token") || "");
  const [notification, setNotification] = useState({
    message: "",
    type: "success",
  });
  const [loading, setLoading] = useState(false);
  const autoConfirmAttempted = useRef(false);
  const autoRequestAttempted = useRef(false);

  const showNotification = (message, type) => {
    setNotification({ message, type });
    window.setTimeout(() => {
      setNotification({ message: "", type: "success" });
    }, 4000);
  };

  // Auto-confirm when both email and token are in the URL
  /**
   * Arriving here from a failed login means the account exists but is unconfirmed, and there is no
   * token in the URL. Nothing used to send a code at that point — the endpoint existed, but only a
   * button the user had to notice and press called it. So "log in and you get a confirmation email"
   * simply did not happen. Requesting it once on arrival makes the promise true.
   */
  useEffect(() => {
    const emailParam = searchParams.get("email");
    const tokenParam = searchParams.get("token");
    if (!emailParam || tokenParam || autoRequestAttempted.current) return;

    autoRequestAttempted.current = true;
    (async () => {
      try {
        await requestEmailConfirmation(emailParam);
        showNotification(
          t("login.confirm_auto_sent", {
            email: emailParam,
            defaultValue: "We have sent a confirmation link to {{email}}.",
          }),
          "success"
        );
      } catch {
        // Rate limiting or a transient failure must not block the page — the manual
        // "send again" button is still there.
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const emailParam = searchParams.get("email");
    const tokenParam = searchParams.get("token");
    if (emailParam && tokenParam && !autoConfirmAttempted.current) {
      autoConfirmAttempted.current = true;
      setLoading(true);
      confirmEmail({ email: emailParam, token: tokenParam })
        .then(() => {
          showNotification(
            t("login.confirm_success", "Email confirmed! Redirecting to login..."),
            "success"
          );
          window.setTimeout(() => navigate("/login"), 1500);
        })
        .catch(() => {
          showNotification(
            t("login.confirm_error", "Confirmation failed. Please try again or request a new link."),
            "error"
          );
        })
        .finally(() => setLoading(false));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const requestToken = async () => {
    try {
      await requestEmailConfirmation(email);
      showNotification(
        t("login.confirm_sent", "Confirmation link sent if email exists."),
        "success"
      );
    } catch {
      showNotification(
        t("login.confirm_error", "Could not request confirmation."),
        "error"
      );
    }
  };

  const handleConfirm = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await confirmEmail({ email, token });
      showNotification(
        t("login.confirm_success", "Email confirmed!"),
        "success"
      );
      window.setTimeout(() => navigate("/login"), 1000);
    } catch {
      showNotification(
        t("login.confirm_error", "Confirmation failed."),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // If auto-confirming from URL, show a simple loading state
  const hasUrlParams = searchParams.get("email") && searchParams.get("token");

  return (
    <S.Page>
      <S.FormWrapper>
        <S.FormHeader>
          <h1>{t("login.confirm_title") || "Confirm Email"}</h1>
        </S.FormHeader>
        {hasUrlParams && loading ? (
          <S.Form as="div">
            <p style={{ textAlign: "center", padding: "1rem 0" }}>
              {t("login.confirming", "Confirming your email...")}
            </p>
            {notification.message ? (
              <S.Notification $type={notification.type} role="status">
                {notification.message}
              </S.Notification>
            ) : null}
          </S.Form>
        ) : (
          <S.Form onSubmit={handleConfirm}>
            <Input
              id="email"
              name="email"
              label={t("login.email") || "Email"}
              placeholder={t("login.email_placeholder") || "example@example.com"}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Button type="button" variant="ghost" fullWidth onClick={requestToken}>
              {t("login.confirm_request", "Send confirmation link")}
            </Button>
            <Input
              id="token"
              name="token"
              label={t("login.confirm_token", { defaultValue: "Confirmation code" })}
              placeholder={t("login.confirm_token_placeholder", {
                defaultValue: "Paste the code from the email",
              })}
              type="text"
              autoComplete="one-time-code"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              required
            />
            {notification.message ? (
              <S.Notification $type={notification.type} role="status">
                {notification.message}
              </S.Notification>
            ) : null}
            <Button type="submit" variant="primary" fullWidth disabled={loading || !token}>
              {t("login.confirm_submit", "Confirm Email")}
            </Button>
          </S.Form>
        )}
        <S.ToggleText>
          {t("login.toggle_existing") || "Already confirmed?"}{" "}
          <S.ToggleAction type="button" onClick={() => navigate("/login")}>
            {t("login.toggle_login") || "Log In"}
          </S.ToggleAction>
        </S.ToggleText>
      </S.FormWrapper>
    </S.Page>
  );
};

export default ConfirmEmailPage;
